import type { ChangeEvent } from 'react';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { useGettingStartedGuide } from '@hotjar/webapp/hooks/useGettingStartedGuide';
import type { Recording } from '@hotjar/webapp/domains/recordings/types';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { tokens, Loader, Select } from '@hotjar/hotsauce';
import { getRecordingPath } from '@hotjar/recordings/src/utils/getRecordingPath';
import { useSelectRecordingsPopupStatus } from '@hotjar/webapp/features/Activation/RecordingsPopup/useFirstRecordingStatus';
import { useSegments } from '@hotjar/webapp/domains/segments/hooks/useSegments';
import { useCurrentSite } from '@hotjar/site';
import { usePrevious } from '@hotjar/webapp/hooks/usePrevious';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { PLAYBACKS } from '@hotjar/webapp/constants/routes';
import { Card } from '@hotjar/webapp/components/Card';
import { useTrackingCode } from '@hotjar/webapp/hooks/useTrackingCode';
import type { Segment } from '@hotjar/webapp/features/Recordings/types';
import { DEFAULT_SEGMENT } from '@hotjar/webapp/domains/segments/constant';
import {
  useRecordingsWidgetData,
  clearRecordingsWidgetData,
} from '@hotjar/webapp/features/Activation/hooks/useRecordingsWidget';
import { useRecordingsStatefulCounter } from '@hotjar/webapp/recordings/useStatefulPlaybackTracker';
import type { Recording as PlaybackRecording } from '@hotjar/webapp/features/Playbacks/types';
import { LocalStorage } from '@hotjar/insights-common/lib/storage-factory';
import { getFilters } from '@hotjar/webapp/domains/recordings/api/recordings';
import {
  RECORDING_INDEX_STORAGE_KEY,
  SEARCH_CONTEXT_STORAGE_KEY,
} from '@hotjar/recordings/src/store/player/slices/recordingsListSlice/constants';
import { RecordingsCardList } from './RecordingsCardList';
import { NewEmptyWidget, EMPTY_STATE_KIND, EMPTY_STATE_TOOL } from '../NewEmptyWidget';
import { getForYouExperimentWidgetHeaderConfig } from '../../utils';

const SegmentDropdown = styled(Select)`
  max-width: 200px;
`;

const StyledLoader = styled(Loader)`
  padding: ${tokens.spacer.x4};
`;

const BodyLoader = styled(StyledLoader)`
  height: 200px;
`;

export const RecordingsCard = ({
  inForYouExperiment,
}: {
  inForYouExperiment?: boolean;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const currentTimestamp = Math.floor(Date.now() / 1000);
  const daysAgoTimestamp = currentTimestamp - 24 * 60 * 60;
  const recordingsRequestTimeRangeRef = useRef([daysAgoTimestamp, currentTimestamp]);
  const [selectedSegment, setSelectedSegment] = useState<Segment | null>(null);
  const { data: currentSite, isLoading: isCurrentSiteLoading } = useCurrentSite();
  const { onIncrement } = useRecordingsStatefulCounter();
  const { click: trackClickEvent } = useTrack();
  const { markRecordingAsSeen } = useGettingStartedGuide();
  const { getState: getTrackingCodeState } = useTrackingCode();
  const trackingCodeState = getTrackingCodeState(currentSite?.lastData);
  const { data: recordings, isLoading: isRecordingsDataLoading } = useRecordingsWidgetData(
    currentSite?.id,
    selectedSegment,
    'dashboard',
  );
  const {
    data: segments,
    isLoading: isSegmentDataLoading,
    error: segmentsError,
  } = useSegments(currentSite?.id);
  const isLoading = isCurrentSiteLoading || isRecordingsDataLoading || isSegmentDataLoading;
  const { data: firstRecordingStatus } = useSelectRecordingsPopupStatus();
  const prevFirstRecordingPresent = usePrevious(firstRecordingStatus?.first_recording_present);
  const trackClick = (elementName: string) =>
    trackClickEvent(elementName, 'Overview Recordings Card', {});
  const trackSelectedSegment = (nextSelectedSegment: string) =>
    trackClickEvent(`Selected segment ${nextSelectedSegment}`, 'Overview Recordings Card', {});
  const onRecordingsClick = (recording: Recording, index: number) => {
    trackClick(`Go to recording ${index + 1}`);

    onIncrement();
    markRecordingAsSeen();

    LocalStorage.set(SEARCH_CONTEXT_STORAGE_KEY, {
      jsonFilters: getFilters(selectedSegment),
      sortBy: '-relevance_score',
      recordingsRequestTimeRange: recordingsRequestTimeRangeRef.current,
    });

    LocalStorage.set(
      RECORDING_INDEX_STORAGE_KEY,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      recordings.findIndex((r) => r.id === recording.id),
    );

    window.open(getRecordingPath(recording as unknown as PlaybackRecording, 'overview'), '_blank');
  };

  const onSegmentChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (currentSite?.id) {
      clearRecordingsWidgetData(currentSite.id);
    }

    const nextSelectedSegment =
      segments?.find((segment) => segment.id === Number(e.target.value)) || null;

    if (nextSelectedSegment?.name) {
      trackSelectedSegment(nextSelectedSegment.name);
    }
    setSelectedSegment(nextSelectedSegment);
  };

  const getSelectedSegmentTime = () => {
    return selectedSegment?.id === DEFAULT_SEGMENT.id
      ? t('activation:dashboard.recordings-card.sub-title')
      : t('activation:dashboard.recordings-card.segment-time');
  };

  useEffect(() => {
    if (segments?.length) {
      setSelectedSegment(segments[0]);
    }
  }, [segments]);

  useEffect(() => {
    if (segmentsError) {
      setSelectedSegment(DEFAULT_SEGMENT);
    }
  }, [segmentsError]);

  useEffect(() => {
    // when first recording just arrives
    // we clear widget cache and refetch
    if (
      prevFirstRecordingPresent === false &&
      !!firstRecordingStatus?.first_recording_present &&
      currentSite?.id
    ) {
      clearRecordingsWidgetData(currentSite.id, { refetchActive: true, refetchInactive: true });
    }
  }, [firstRecordingStatus?.first_recording_present, prevFirstRecordingPresent, currentSite?.id]);

  if (firstRecordingStatus?.recording_mode === 'off') {
    return null;
  }

  return (
    <Card>
      {!currentSite ? (
        <StyledLoader />
      ) : (
        <>
          <Card.Header
            icon={getForYouExperimentWidgetHeaderConfig('recordings', inForYouExperiment).icon}
            variant={
              getForYouExperimentWidgetHeaderConfig('recordings', inForYouExperiment).variant
            }
            title={t('activation:dashboard.recordings-card.title')}
            href={getRoute(PLAYBACKS.LIST.path, {
              siteId: currentSite.id,
            })}
            onClick={() => trackClick('Card Title')}
            subtitle={
              trackingCodeState.installed && !!recordings?.length
                ? getSelectedSegmentTime()
                : undefined
            }
          >
            {segments && segments.length > 1 && selectedSegment ? (
              <SegmentDropdown
                size="s"
                data-testid="segmentSelector"
                defaultValue={selectedSegment?.id}
                onChange={onSegmentChange}
              >
                {segments.map(({ id, name }) => (
                  <option key={id} value={id}>
                    {/* translate only default segments */}
                    {id < 0 ? t(name) : name}
                  </option>
                ))}
              </SegmentDropdown>
            ) : null}
          </Card.Header>
          {isLoading || !recordings ? (
            <BodyLoader />
          ) : (
            <>
              {!trackingCodeState.installed ? (
                <NewEmptyWidget tool={EMPTY_STATE_TOOL.recordings} kind={EMPTY_STATE_KIND.NO_TC} />
              ) : (
                <RecordingsCardList
                  selectedSegmentId={selectedSegment?.id}
                  onRecordingsClick={onRecordingsClick}
                  onFooterButtonClick={() => trackClick('Card Footer Button')}
                  recordingsList={recordings}
                />
              )}
            </>
          )}
        </>
      )}
    </Card>
  );
};
